<template>
    <!--
        TODO: Pass correctly parent down; probably regular JsonSchemaForm has to use CustomForm behind.
              At the moment, it's just using the old component behind.
              Should finally be :form-element-component="SelfComponent", with "field" etc.
    -->
    <!-- PRODUCT-26110 Task 5 (INSTANCE_ATTRS_CLASS_STYLE): Vue 3 folds class/style
         into $attrs and stops auto-applying them to root when inheritAttrs is false,
         so a caller's `class="flex-1"` (etc.) would silently stop reaching this
         wrapper. Bind them explicitly to keep the old Vue 2 fallthrough behavior. -->
    <div :class="$attrs.class" :style="$attrs.style">
        <component
            :is="customFormElementComponent"
            :id="field.id"
            :value="field.value"
            :wrap-value-mode="wrapValueMode"
            :form-element-component="computedBasedComponent"
            :custom-component-input="customComponentInput"
            :form="field.form"
            :touched="touched || field.touched || computedParent.touched"
            :depth="computedDepth"
            :required="required || field.required"
            :disabled="disabled || field.disabled || computedParent.disabled"
            :strings="computedStrings"
            :deletable="field.deletable"
            :rbac-disabled="rbacDisabled"
            :custom-title="customTitle"
            :show-validation-errors="showValidationErrors"
            v-bind="$attrs"
            @input="field.value = $event"
            @touch="touch"
        >
            <template #icon>
                <slot name="icon" />
            </template>
            <template #deleteIcon>
                <slot name="deleteIcon" />
            </template>
        </component>
    </div>
</template>

<script lang="ts">
    import type { Component as VueComponent } from 'vue';

    import { Component, Prop, Watch } from 'vue-property-decorator';
    // Vue 3: 'Component' type imported directly; the ambient Vue-2 namespace types
    // (transitive @types straggler) must not be used for component-map typing (PRODUCT-26110).

    import { Pointer } from '@evidentid/json-schema/FormController';

    import AbstractJsonSchemaFormElement from './AbstractJsonSchemaFormElement';
    import JsonSchemaFormElement from './JsonSchemaFormElement.vue';
    import { JsonSchemaFormStrings } from './types';

    interface JsonSchemaCustomFormReference {
        touched: boolean;
        disabled: boolean;
        strings: Partial<JsonSchemaFormStrings>;
    }

    function isCustomJsonSchemaForm(element: any): element is JsonSchemaCustomFormReference {
        return Boolean(element?.$customJsonSchemaForm);
    }

    @Component({
        components: {
            JsonSchemaFormElement,
        },
        inheritAttrs: false,
    })
    export default class JsonSchemaCustomFormElement extends AbstractJsonSchemaFormElement {
        @Prop()
        private baseComponent?: VueComponent | null;

        @Prop({ type: Object, default: null })
        private parent!: JsonSchemaCustomFormReference | null;

        @Prop(Object)
        private field!: Pointer;

        @Prop({ type: Number, default: null })
        private depth!: number | null;

        private unsubscribe: (() => void) | null = null;

        private unmounted(): void {
            this.unsubscribe?.();
            this.unsubscribe = null;
        }

        private get computedBasedComponent() {
            return this.baseComponent || JsonSchemaFormElement;
        }

        private get computedDepth(): number {
            return this.depth ?? this.field.depth;
        }

        private get computedStrings(): Partial<JsonSchemaFormStrings> {
            return {
                ...this.computedParent.strings,
                ...this.strings,
            };
        }

        @Watch('field', { immediate: true })
        private subscribeToField(): void {
            this.unsubscribe?.();
            // PRODUCT-26110 Phase 2B2: FormController fires onChange synchronously from
            // its reactive set traps, which can run while this instance is still setting
            // up -- before Vue 3 populates its render effect. A synchronous
            // `$forceUpdate()` then queues a null scheduler job ("Cannot read properties
            // of null (reading 'flags')"). Defer to the next tick, like
            // JsonSchemaCustomForm.scheduleForceUpdate.
            this.unsubscribe = this.field?.onChange(() => {
                void this.$nextTick().then(() => {
                    if (this.unsubscribe) {
                        this.$forceUpdate();
                    }
                });
            }) || null;
        }

        private get dynamicParent(): JsonSchemaCustomFormReference | null {
            let currentParent = this.$parent;
            while (currentParent && currentParent !== this.$root && !isCustomJsonSchemaForm(currentParent)) {
                currentParent = currentParent.$parent;
            }
            return isCustomJsonSchemaForm(currentParent) ? currentParent : null;
        }

        private get computedParent(): JsonSchemaCustomFormReference {
            const parent = this.parent || this.dynamicParent;
            if (!parent) {
                throw new Error('Couldn\'t find parent JsonSchemaCustomForm for element. Verify if it is built fine.');
            }
            return parent;
        }

        private get customFormElementComponent(): VueComponent {
            return (
                this.getCustomComponent?.(this.field.form, this.components) ||
                this.components[this.field.form.type]
            );
        }

        private touch(): void {
            this.field.touch();
        }
    }
</script>
